import { CancelActiveInvocationsResponse } from "@/shared/domain/actions";
import { AISummary } from "@/shared/domain/ai_summary.ts";
import { EventRecord } from "@/shared/domain/events";
import {
  HttpClient,
  type PaginatedResponse,
  robotoHeaders,
} from "@/shared/http";
import { parseTimestampsAsBigInt } from "@/shared/json";

import { FileRecord } from "../files/FileRecord";

import { DatasetRecord, UpdateDatasetRequest } from "./DatasetRecord";

interface Options {
  abortSignal: AbortSignal;
  pageToken: string;
  limit: number;
  resourceOwnerId: string;
  searchParams: URLSearchParams;
}

interface ListFilesOptions {
  abortSignal: AbortSignal;
  pageToken: string;
  includePatterns: string[];
  excludePatterns: string[];
}

export class DatasetService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async cancelAssociatedInvocations(
    datasetId: string,
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<CancelActiveInvocationsResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/invocations/cancel`,
    );

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ orgId }),
      signal: options?.abortSignal,
    });

    return await response.json<CancelActiveInvocationsResponse>();
  }

  public async countAssociatedActiveInvocations(
    datasetId: string,
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<number> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/invocations/count/active`,
    );

    const response = await this.#httpClient.get(requestUrl, {
      headers: robotoHeaders({ orgId }),
      signal: options?.abortSignal,
    });

    return Number.parseInt(await response.text());
  }

  public async getEventsForDataset(
    datasetId: string,
    options?: Partial<Options>,
  ): Promise<PaginatedResponse<EventRecord>> {
    const searchParams = new URLSearchParams();

    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }

    if (options?.limit !== undefined) {
      searchParams.append("limit", options.limit.toString());
    }

    const url = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/events`,
      searchParams,
    );

    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });

    return await response.json<PaginatedResponse<EventRecord>>(
      parseTimestampsAsBigInt,
    );
  }

  public async getTagsForOrg(options?: Partial<Options>): Promise<string[]> {
    if (!options?.resourceOwnerId) {
      throw Error("getTagsForOrg requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl("v1/datasets/tags");
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });

    return await response.json<string[]>();
  }

  public async getMetadataKeysForOrg(
    options?: Partial<Options>,
  ): Promise<string[]> {
    if (!options?.resourceOwnerId) {
      throw Error(
        "getMetadataKeysForOrg requires an Org ID, none was provided",
      );
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/datasets/metadata/keys",
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });

    return await response.json<string[]>();
  }

  public async deleteDataset(
    datasetId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    if (!options?.resourceOwnerId) {
      throw Error("deleteDataset requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}`,
    );
    const response = await this.#httpClient.delete(requestUrl, {
      idempotent: false,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });

    await response.throwIfError();
  }

  public async getDataset(
    datasetId: string,
    options?: Partial<Options>,
  ): Promise<DatasetRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    return await response.json<DatasetRecord>();
  }

  public async updateDataset(
    datasetId: string,
    updates: UpdateDatasetRequest,
    options?: Partial<Options>,
  ): Promise<DatasetRecord> {
    if (!options?.resourceOwnerId) {
      throw Error("updateDataset requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}`,
    );

    const response = await this.#httpClient.put(requestUrl, {
      idempotent: true,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
      body: JSON.stringify(updates),
    });

    return response.json<DatasetRecord>();
  }

  public async getSummary(
    datasetId: string,
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<AISummary> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/summary`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({}),
    });
    return response.json<AISummary>();
  }

  public async generateSummary(
    datasetId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      systemPrompt?: string;
    }>,
  ): Promise<AISummary> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/summary`,
    );

    const overrides = options?.systemPrompt
      ? { system_prompt: options.systemPrompt }
      : undefined;

    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({}),
      body: overrides && JSON.stringify(overrides),
    });
    return response.json<AISummary>();
  }

  /**
   * List files in a dataset with optional pattern-based filtering.
   *
   * Returns all files in the dataset that match the specified include patterns
   * while excluding those that match exclude patterns. Uses gitignore-style
   * pattern matching for flexible file selection.
   *
   * @param datasetId - The ID of the dataset to list files from
   * @param options - Optional parameters for filtering and pagination
   * @param options.includePatterns - List of gitignore-style patterns for files to include.
   *                                  If undefined, all files are considered.
   * @param options.excludePatterns - List of gitignore-style patterns for files to exclude.
   *                                  Takes precedence over include patterns.
   * @param options.pageToken - Token for retrieving the next page of results
   * @param options.abortSignal - Signal to abort the request
   * @returns A paginated response containing file records
   *
   * @example
   * ```typescript
   * // List all files
   * const allFiles = await datasetService.listFiles("ds_abc123");
   *
   * // List only image files, excluding back camera
   * const imageFiles = await datasetService.listFiles("ds_abc123", {
   *   includePatterns: ["**\/*.png", "**\/*.jpg"],
   *   excludePatterns: ["**\/back_camera\/**"]
   * });
   * ```
   */
  public async listFiles(
    datasetId: string,
    options?: Partial<ListFilesOptions>,
  ): Promise<PaginatedResponse<FileRecord>> {
    const requestBody: {
      page_token?: string;
      include_patterns?: string[];
      exclude_patterns?: string[];
    } = {};

    if (options?.pageToken !== undefined) {
      requestBody.page_token = options.pageToken;
    }

    if (options?.includePatterns !== undefined) {
      requestBody.include_patterns = options.includePatterns;
    }

    if (options?.excludePatterns !== undefined) {
      requestBody.exclude_patterns = options.excludePatterns;
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/files/query`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({}),
      body: JSON.stringify(requestBody),
      idempotent: true,
    });
    return response.json<PaginatedResponse<FileRecord>>();
  }

  /**
   * List all files in a dataset with optional pattern-based filtering.
   *
   * Automatically handles pagination and returns all file records that match
   * the specified patterns. This method will continue fetching pages until
   * all files have been retrieved.
   *
   * Uses the listFiles() method internally.
   */
  public async listAllFiles(
    datasetId: string,
    options?: Partial<Omit<ListFilesOptions, "pageToken">>,
  ): Promise<FileRecord[]> {
    const allFiles: FileRecord[] = [];
    let pageToken: string | undefined = undefined;

    while (true) {
      const paginatedResponse = await this.listFiles(datasetId, {
        ...options,
        pageToken,
      });

      allFiles.push(...paginatedResponse.items);

      if (paginatedResponse.next_token) {
        pageToken = paginatedResponse.next_token;
      } else {
        break;
      }
    }

    return allFiles;
  }
}
